<template>
  <FilePond
    ref="pond"
    class="cursor-pointer"
    :allow-paste="!preventCopyPaste"
    :allow-multiple="true"
    :instant-upload="true"
    :server="server"
    :label-idle="t('components.shared.MultiUpload.idle')"
    :label-file-processing="t('components.shared.MultiUpload.processing')"
    :label-tap-to-undo="t('components.shared.MultiUpload.undo')"
    :label-tap-to-cancel="t('components.shared.MultiUpload.cancel')"
    :label-tap-to-retry="t('components.shared.MultiUpload.retry')"
    :label-file-processing-complete="t('components.shared.MultiUpload.complete')"
    :label-file-processing-aborted="t('components.shared.MultiUpload.uploadCancelled')"
    :label-file-processing-error="t('components.shared.MultiUpload.uploadError')"
    :label-file-processing-revert-error="t('components.shared.MultiUpload.revertError')"
    :label-file-load-error="t('components.shared.MultiUpload.loadError')"
    :label-file-remove-error="t('components.shared.MultiUpload.removeError')"
    :label-invalid-field="t('components.shared.MultiUpload.invalidField')"
    :label-file-type-not-allowed="t('components.shared.MultiUpload.invalidFileType')"
    :file-validate-type-label-expected-types="t('components.shared.MultiUpload.allowedFileTypes')"
    :accepted-file-types="
      mimeTypeFilters ?? 'image/*,video/*,audio/*,application/pdf,application/json,text/*'
    "
    :max-files="maxFileUpload"
    @processfile="handleProcessFile"
    @addfilestart="handleUploadStart"
    @processfileabort="handleUploadEnd"
    @processfileprogress="handleProcessFileProgress"
  />
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import api from "@/api/api";
import vueFilePond from "vue-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import type { FilePondOptions } from "filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import { type ComponentPublicInstance, computed, type PropType, ref } from "vue";
import {
  FMFolderType,
  useFMFileCreateMutation,
  useFMSpecialFolderIdQuery,
  type FMFolderContentFileFragment,
} from "@/graphql";
import type { DocumentNode } from "graphql";
import { pdfjsLib } from "./FMFileViewer/pdf/usePdfDocument";

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);
type UploadTarget = {
  uuid: string;
  url: string;
  thumbnailURL: string;
  thumbnailUUID: string;
};

type UploadableFile = Blob & {
  name: string;
  lastModified?: number;
  type: string;
};

const uploadInProgress = defineModel("uploadInProgress", {
  type: Boolean,
  required: false,
  default: false,
});

const emit = defineEmits<{
  fileCreated: [FMFolderContentFileFragment];
}>();

const props = defineProps({
  preventCopyPaste: {
    type: Boolean,
    required: false,
    default: false,
  },
  removeFileAfterEmit: {
    type: Boolean,
    required: false,
    default: false,
  },
  mimeTypeFilters: {
    type: String,
    required: false,
    default: undefined,
  },
  refetchAwait: {
    type: Array as PropType<
      {
        query: DocumentNode;
        variables: any;
      }[]
    >,
    required: false,
    default: undefined,
  },
  folderId: {
    type: Number,
    required: false,
    default: undefined,
  },
  maxFileUpload: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const { result: resultTempFolder } = useFMSpecialFolderIdQuery(
  {
    types: FMFolderType.TEMPORARY,
  },
  () => ({
    fetchPolicy: "cache-first",
    enabled: props.folderId == null,
  }),
);

const tempFolderId = computed(() => props.folderId ?? resultTempFolder.value?.FMFolders?.[0]?.id);

const { mutate: mutateFileCreate } = useFMFileCreateMutation();

const pendingUploads = ref(new Set<string>());

function updateUploadInProgress() {
  uploadInProgress.value = pendingUploads.value.size > 0;
}

function getFileItemKey(fileItem: { id?: string; filename?: string }) {
  return fileItem.id ?? fileItem.filename ?? "";
}

function handleUploadStart(fileItem: { id?: string; filename?: string }) {
  const key = getFileItemKey(fileItem);
  if (key === "") {
    uploadInProgress.value = true;
    return;
  }

  pendingUploads.value.add(key);
  updateUploadInProgress();
}

function handleUploadEnd(fileItem: { id?: string; filename?: string }) {
  const key = getFileItemKey(fileItem);
  if (key !== "") {
    pendingUploads.value.delete(key);
  }
  updateUploadInProgress();
}

function handleProcessFileProgress(fileItem: { id?: string; filename?: string }) {
  handleUploadStart(fileItem);
}

async function upsertUploadedFile(file: {
  fileName: string;
  mimeType: string;
  fileUUID: string;
  hasThumbnail: boolean;
  originalFileEnding: string;
}) {
  if (tempFolderId.value == null) {
    throw new Error("Temporary folder ID not available");
  }
  const fileCreated = await mutateFileCreate(
    {
      files: [
        {
          fileName: file.fileName,
          fileUUID: file.fileUUID,
          mimeType: file.mimeType,
          originalFileName: file.fileName,
          folderId: tempFolderId.value,
          originalFileEnding: file.originalFileEnding,
          hasThumbnail: file.hasThumbnail,
        },
      ],
    },
    {
      refetchQueries: props.refetchAwait,
      awaitRefetchQueries: true,
    },
  );

  removeFileByUuid(file.fileUUID);

  const createdFile = fileCreated?.data?.FMFileCreate?.[0];
  if (createdFile != null) {
    emit("fileCreated", createdFile);
  }
}

async function handleProcessFile(
  error: unknown,
  fileItem: {
    id?: string;
    fileExtension: string;
    filenameWithoutExtension: string;
    filename: string;
    fileType: string;
    file?: { type: string };
    serverId?: string;
  },
) {
  try {
    if (error != null || !fileItem?.serverId) {
      return;
    }

    let fileName = fileItem.filenameWithoutExtension ?? fileItem.filename ?? "";

    if (!fileName || fileName === "image") {
      fileName = `image_${new Date().toISOString()}`;
    }

    await upsertUploadedFile({
      fileName: fileName,
      originalFileEnding: fileItem.fileExtension,
      mimeType: fileItem.fileType ?? fileItem.file?.type ?? "",
      fileUUID: String(fileItem.serverId),
      hasThumbnail: hasThumbnail(fileItem.fileType ?? fileItem.file?.type ?? ""),
    });
  } finally {
    handleUploadEnd(fileItem);
  }
}

type FilePondInstance = ComponentPublicInstance<{
  getFiles: () => any[];
  removeFile: (query?: string | number | object) => void;
  addFile: (source: unknown, options?: unknown) => Promise<unknown>;
}>;

const pond = ref<FilePondInstance | null>(null);

function removeFileByUuid(uuid: string) {
  const pondInstance = pond.value;
  if (!pondInstance) {
    return;
  }

  const items = pondInstance.getFiles?.() ?? [];
  const item = items.find((entry: any) => String(entry.serverId ?? entry.source) === uuid);

  if (item != null) {
    pondInstance.removeFile?.(item.id);
  }
}

function getErrorMessage(err: unknown, fallback = "Upload failed"): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  if (typeof err === "string" && err.length > 0) {
    return err;
  }
  return fallback;
}

function uploadBlobWithXhr(params: {
  url: string;
  blob: Blob;
  contentType: string;
  onProgress?: (loaded: number, total: number, lengthComputable: boolean) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open("PUT", params.url);
    request.setRequestHeader("Content-Type", params.contentType);

    request.upload.onprogress = (event) => {
      params.onProgress?.(event.loaded, event.total, event.lengthComputable);
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${request.status}`));
      }
    };

    request.onerror = () => reject(new Error("Network error during upload"));
    request.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

    params.signal?.addEventListener("abort", () => {
      if (request.readyState !== XMLHttpRequest.DONE) {
        request.abort();
      }
    });

    request.send(params.blob);
  });
}

async function createImageThumbnail(file: UploadableFile): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  const maxSide = 320;
  const scale = Math.min(maxSide / bitmap.width, maxSide / bitmap.height, 1);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (ctx == null) {
    throw new Error("Could not create canvas context");
  }

  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.8);
  });

  if (blob == null) {
    throw new Error("Could not create image thumbnail");
  }

  return blob;
}

async function createVideoThumbnail(file: UploadableFile): Promise<Blob> {
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;

  const objectUrl = URL.createObjectURL(file);
  video.src = objectUrl;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("Could not load video for thumbnail"));
    });

    const targetTime = Math.min(1, Number.isFinite(video.duration) ? video.duration / 2 : 1);

    await new Promise<void>((resolve, reject) => {
      video.currentTime = targetTime;
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error("Could not seek video for thumbnail"));
    });

    const maxSide = 320;
    const scale = Math.min(maxSide / video.videoWidth, maxSide / video.videoHeight, 1);
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (ctx == null) {
      throw new Error("Could not create canvas context");
    }

    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.8);
    });

    if (blob == null) {
      throw new Error("Could not create video thumbnail");
    }

    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function createPdfThumbnail(file: UploadableFile): Promise<Blob | null> {
  const buffer = await file.arrayBuffer();

  const pdf = await pdfjsLib.getDocument({
    data: buffer,
  }).promise;

  try {
    const page = await pdf.getPage(1);

    const initialViewport = page.getViewport({ scale: 1 });

    const maxSide = 320;
    const scale = Math.min(maxSide / initialViewport.width, maxSide / initialViewport.height, 1);

    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const ctx = canvas.getContext("2d");
    if (ctx == null) {
      throw new Error("Canvas context unavailable");
    }

    await page.render({
      canvas,
      canvasContext: ctx,
      viewport,
    }).promise;

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.85);
    });

    if (blob == null) {
      throw new Error("Could not create PDF thumbnail");
    }

    return blob;
  } finally {
    await pdf.destroy();
  }
}

function hasThumbnail(mimeType: string) {
  return (
    mimeType.startsWith("image/") || mimeType.startsWith("video/") || mimeType === "application/pdf"
  );
}

async function createThumbnail(file: UploadableFile): Promise<Blob | null> {
  if (!hasThumbnail(file.type)) {
    return null;
  }
  if (file.type.startsWith("image/")) {
    return createImageThumbnail(file);
  }

  if (file.type.startsWith("video/")) {
    return createVideoThumbnail(file);
  }

  if (file.type === "application/pdf") {
    return createPdfThumbnail(file);
  }

  return null;
}

const server = {
  process: (_fieldName, file, _metadata, load, error, progress, abort) => {
    const abortController = new AbortController();

    const run = async () => {
      try {
        const uploadTarget = (await api.minio.getURL()) as UploadTarget;

        const thumbnail = await createThumbnail(file);

        const uploads: Promise<void>[] = [
          uploadBlobWithXhr({
            url: uploadTarget.url,
            blob: file,
            contentType: file.type || "application/octet-stream",
            signal: abortController.signal,
            onProgress: (loaded, total, lengthComputable) => {
              progress(lengthComputable, loaded, total);
            },
          }),
        ];

        if (thumbnail != null) {
          uploads.push(
            uploadBlobWithXhr({
              url: uploadTarget.thumbnailURL,
              blob: thumbnail,
              contentType: "image/jpeg",
              signal: abortController.signal,
            }),
          );
        }

        await Promise.all(uploads);

        load(uploadTarget.uuid);
      } catch (err: unknown) {
        if (abortController.signal.aborted) {
          return;
        }
        error(getErrorMessage(err));
      }
    };

    void run();

    return {
      abort: () => {
        abortController.abort();
        abort();
      },
    };
  },

  revert: async (uniqueFileId, load, error) => {
    try {
      const uuid = String(uniqueFileId);

      await Promise.allSettled([api.minio.delete(uuid), api.minio.delete(`${uuid}_thumbnail`)]);

      load();
    } catch (err: unknown) {
      error(getErrorMessage(err, "Delete failed"));
    }
  },
} satisfies NonNullable<FilePondOptions["server"]>;

async function addFile(file: File | Blob) {
  await pond.value?.addFile(file);
}

defineExpose({
  addFile,
});

const { t } = useTranslation();
</script>
<style>
.filepond--item {
  width: calc(100% / 5 - 0.5em);
}

.filepond--credits {
  display: none;
}

.filepond--root {
  margin-bottom: 0;
}
</style>
